import type { Vec2, Vec3 } from "../types";

export const EPSILON = 1e-7;

export const add3 = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const sub3 = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const mul3 = (a: Vec3, scalar: number): Vec3 => [a[0] * scalar, a[1] * scalar, a[2] * scalar];
export const dot3 = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const cross3 = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
export const length3 = (a: Vec3): number => Math.sqrt(dot3(a, a));
export const normalize3 = (a: Vec3): Vec3 => {
  const length = length3(a);
  return length < EPSILON ? [0, 1, 0] : mul3(a, 1 / length);
};
export const lerp3 = (a: Vec3, b: Vec3, t: number): Vec3 => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];
export const average3 = (values: Vec3[]): Vec3 => {
  if (!values.length) return [0, 0, 0];
  return mul3(values.reduce<Vec3>((sum, value) => add3(sum, value), [0, 0, 0]), 1 / values.length);
};
export const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));
export const rotate2 = (value: Vec2, degrees: number): Vec2 => {
  const radians = degrees * Math.PI / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return [value[0] * cos - value[1] * sin, value[0] * sin + value[1] * cos];
};
export const faceNormal = (positions: Vec3[]): Vec3 => {
  if (positions.length < 3) return [0, 1, 0];
  let normal: Vec3 = [0, 0, 0];
  for (let i = 0; i < positions.length; i += 1) {
    const current = positions[i];
    const next = positions[(i + 1) % positions.length];
    normal = add3(normal, [
      (current[1] - next[1]) * (current[2] + next[2]),
      (current[2] - next[2]) * (current[0] + next[0]),
      (current[0] - next[0]) * (current[1] + next[1]),
    ]);
  }
  return normalize3(normal);
};
export const vecKey = (value: Vec3): string => value.map((part) => part.toFixed(6)).join(",");

